import { pageErrorRedirect } from '@core/router/utils';
import ErrorService from '@core/services/ErrorService';
import { getFilterOperator } from '@core/services/filter';
import { setBreadcrumbs, hideBreadcrumbs, showBreadcrumbs } from '@core/services/ui';
import { getValidator } from '@core/validators';
import BaseModel from '@core/model';

import { removeNull, clone } from '@utils/object';
import { wait } from '@utils/async';
import { getQueryParam, replaceQueryParam } from '@utils/browser';
import { now, getTaxYear, toDatetime, toDate, timeUntil, toIso8601 } from '@utils/date';
import { copyToClipboard } from '@utils/dom';
import { getMask } from '@utils/form';
import { getIcon } from '@utils/image';
import { uuid, isEmailMatch, formatString } from '@utils/string';

import money from '@plugins/money';

// eslint-disable-next-line import/order
import Adviser from '@/models/Adviser';
// eslint-disable-next-line import/order
import { Auth } from '@core/auth';
import Investor from '@/models/Investor';
import Organisation from '@/models/Organisation';
import User from '@/models/User';

export default {
    data: () => ({
        isMounted: false,
        uuid: null,
        $delayEmit: () => {},
        $delay: () => {},
        preventEmitEvent: () => false
    }),
    mounted() {
        this.isMounted = true;

        if (this._.isNil(this.uuid)) {
            this.uuid = uuid();
        }

        this.$delayEmit = (event, data, timeout = 300, prevent = false) => {
            return this._.debounce(() => {
                this.emitEvent(event, data, prevent);
            }, timeout)();
        };

        this.$delay = (callback, timeout = 300) => {
            return this._.debounce(() => {
                callback();
            }, timeout)();
        };
    },
    computed: {
        current_user_id() {
            return Auth().user_id;
        },
        current_user() {
            return Auth().user;
        },
        current_organisation_id() {
            return Auth().organisation_id;
        },
        current_organisation() {
            return Auth().organisation;
        },
        current_route() {
            return this.$store.getters['app/current_route'] || this.$route;
        },
        home_route() {
            return Auth().home_route;
        },
        routing() {
            return this.$store.getters['app/routing'];
        },
        colours() {
            return Auth().branding().colours;
        },
        default_colours() {
            return {
                primary: '#5abdf9',
                primaryBackground: '#5ABDF9',
                primaryLight: '#DFEAF1',
                primaryText: '#0670B1'
            };
        }
    },
    methods: {
        emitEvent(event, data = null, prevent = false) {
            if (prevent) return;
            this.$emit(event, data);
        },
        log(...v) {
            console.log(...v);
        },
        Auth,
        getIcon,
        now,
        hasPermission(permission) {
            return Auth().has_permission(permission);
        },
        hasPermissions(permissions) {
            const auth = Auth();
            return permissions.every(permission => auth.has_permission(permission));
        },
        getTaxYear,
        getMask,
        getValidator,
        getFilterOperator,
        removeNull,
        toDatetime,
        toDate,
        toIso8601,
        money,
        wait,
        timeUntil,
        pageErrorRedirect,
        getQueryParam,
        replaceQueryParam,
        copyToClipboard,
        $clone: clone,
        setBreadcrumbs,
        hideBreadcrumbs,
        showBreadcrumbs,
        isEmailMatch,
        formatString,
        handleError(error, data = {}, options = { id: null, notify: true }) {
            return ErrorService.onError(error, data, options);
        },
        toDecimal(value, { precision = 2, defaultValue = null, callback = null }) {
            if (['', null, undefined, NaN].includes(value)) {
                return defaultValue;
            }

            const decimal = this.money(value, { precision });

            if (String(value).startsWith('-0') && decimal.value === 0) {
                return value;
            }

            if (callback) {
                return callback(decimal);
            }

            return decimal;
        },
        findSlot(name = 'default') {
            return !!this.$slots[`${name}`] || !!this.$scopedSlots[`${name}`];
        },
        getParent(name) {
            let parent = this.$parent;

            while (typeof parent !== 'undefined') {
                if (parent.$options.name === name) {
                    return parent;
                } else {
                    parent = parent.$parent;
                }
            }

            return false;
        },
        findElement(selector) {
            return this.$el.querySelector(selector);
        },
        findElements(selector) {
            return this.$el.querySelectorAll(selector);
        },
        findParentElement(selector) {
            return this.$el.closest(selector);
        },
        getWidth($el) {
            try {
                return $el.getBoundingClientRect().width;
            } catch (error) {
                return null;
            }
        },
        isAdviser() {
            return Auth().is_adviser;
        },
        isFundManager() {
            return Auth().is_fund_manager;
        },
        isProvider() {
            return Auth().is_provider;
        },
        isFundManagerOrProvider() {
            return Auth().is_fund_manager || Auth().is_provider;
        },
        isCompany() {
            return Auth().is_company;
        },
        isInvestor() {
            return Auth().is_investor;
        },
        isIndividualInvestor() {
            return Auth().is_individual_investor;
        },
        isXOInvestor() {
            return Auth().is_xo_investor;
        },
        isAdvisedInvestor() {
            return Auth().is_advised_investor;
        },
        isInvestorAuthorised() {
            return Auth().is_investor_authorised;
        },
        isOwner() {
            return Auth().is_owner;
        },
        isSuperuser() {
            return Auth().is_superuser;
        },
        isGrowthInvest() {
            return Auth().is_gi;
        },
        getOrganisationId() {
            return Auth().organisation_id;
        },
        getOrganisationType() {
            return Auth().organisation_type;
        },
        getOrganisation() {
            const organisation = Auth().organisation;
            if (organisation && !(organisation instanceof Organisation)) {
                return new Organisation(organisation);
            }
            return organisation;
        },
        hasTierAccess(level = 0) {
            const organisation = this.getOrganisation();
            if (!organisation) {
                return false;
            }
            return organisation.hasTierAccess(level);
        },
        getAuthInvestor() {
            const investor = Auth().investor;
            if (investor && !(investor instanceof Investor)) {
                return new Investor(investor);
            }
            return investor;
        },
        getAuthAdviser() {
            const adviser = Auth().adviser;
            if (adviser && !(adviser instanceof Adviser)) {
                return new Adviser(adviser);
            }
            return adviser;
        },
        getCurrentUser() {
            const user = Auth().user;
            if (user && !(user instanceof BaseModel)) {
                return new User(user);
            }
            return user;
        },
        getFlatRefs(refs = null, includeChildren = false) {
            refs = refs || this.$refs;
            let flatRefs = {};

            if (!refs || (typeof refs === 'object' && !Object.keys(refs).length)) {
                return flatRefs;
            }

            for (const [key, value] of Object.entries(refs)) {
                if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        flatRefs[`${key}_${index}`] = item;
                    });
                } else if (value && value.$refs && includeChildren) {
                    flatRefs = { ...flatRefs, ...this.getFlatRefs(value.$refs, includeChildren) };
                } else {
                    flatRefs[`${key}`] = value;
                }
            }

            return flatRefs;
        },
        findRef(field, $refs = null) {
            let $ref = null;

            if ($refs === null) {
                $refs = this.$refs;
            }

            if (field in $refs) {
                return $refs[`${field}`];
            }

            for (const key in $refs) {
                if (!$refs[`${key}`]) {
                    continue;
                }

                if ('$refs' in $refs[`${key}`]) {
                    $ref = this.findRef(field, $refs[`${key}`].$refs);
                }
            }

            return $ref || null;
        },
        openMediaLibrary(collection, onSelected = () => {}, multiple = false, preselected = null, heading = null) {
            this.$store.dispatch('app/setMediaLibrary', {
                collection,
                onSelected,
                multiple,
                preselected,
                heading
            });
            this.$store.dispatch('app/setMediaLibraryVisibility', true);
        },
        closeMediaLibrary() {
            this.$store.dispatch('app/resetMediaLibrary');
        },
        openModal(key, props = {}, callback = () => {}) {
            this.$store.dispatch('app/setModal', { key, props, callback });
        },
        closeModal() {
            this.$store.dispatch('app/resetModal');
        },
        //
        setAppLoading(active = true, title = null, text = null, progress = null, buttons = [], success = false) {
            this.$store.dispatch('app/setLoading', { active, title, text, progress, buttons, success });
        },
        setAppLoadingTitle(title) {
            this.$store.dispatch('app/setLoadingTitle', title);
        },
        setAppLoadingText(text) {
            this.$store.dispatch('app/setLoadingText', text);
        },
        setAppLoadingProgress(progress) {
            this.$store.dispatch('app/setLoadingProgress', progress);
        },
        setAppLoadingButtons(buttons) {
            this.$store.dispatch('app/setLoadingButtons', buttons);
        },
        setAppLoadingSuccess(success = true) {
            this.$store.dispatch('app/setLoadingSuccess', success);
        },
        //
        hasAppProgressQueue() {
            return this.$store.getters['app/has_progress_queue'];
        },
        setAppProgressQueue(active = true, title = null, position = 'right', items = []) {
            this.$store.dispatch('app/setProgressQueue', { active, title, position, items });
        },
        setAppProgressQueueTitle(title) {
            this.$store.dispatch('app/setProgressQueueTitle', title);
        },
        setAppProgressQueueItems(items) {
            this.$store.dispatch('app/setProgressQueueItems', items);
        },
        setAppProgressQueuePosition(items) {
            this.$store.dispatch('app/setProgressQueuePosition', items);
        },
        prependAppProgressQueueItems(items) {
            this.$store.dispatch('app/prependProgressQueueItems', items);
        },
        updateAppProgressQueueItem(id, data) {
            this.$store.dispatch('app/updateProgressQueueItem', { id, data });
        },
        //
        hasShortcutKey(key) {
            return this.$store.getters['keyboard/has'](key);
        },
        hasShortcutKeys(keys) {
            return this.$store.getters['keyboard/only'](keys);
        },
        go(url = null) {
            if (typeof url === 'object') {
                try {
                    const route = this.$router.resolve(url);

                    if (!route) {
                        return;
                    }

                    url = route.href;
                } catch (error) {
                    return;
                }
            }

            if (url === null) {
                return;
            }

            if (this.hasShortcutKey('Control')) {
                window.open(url, '_blank');
                return;
            }

            this.$router.push(url);
        },
        hideFreshdeskWidget() {
            if (typeof window.FreshworksWidget === 'function') {
                window.FreshworksWidget('hide');
            }
        },
        showFreshdeskWidget() {
            if (typeof window.FreshworksWidget === 'function') {
                window.FreshworksWidget('show');
            }
        },
        async focusInputByRef(ref, elementSelector = 'input') {
            await this.$nextTick();

            if (!this.$refs[`${ref}`]) {
                return;
            }

            if (!this.$refs[`${ref}`].$el) {
                return;
            }

            const $input = this.$refs[`${ref}`].$el.querySelector(elementSelector);

            if (!$input) {
                return;
            }

            $input.focus();
        },
        toModel(value, model) {
            if (value === null || value === undefined) {
                return null;
            }

            if (typeof model !== 'function' || !(model.prototype instanceof BaseModel)) {
                console.warn('toModel expects a class that extends the BaseModel class');
                return null;
            }

            if (value instanceof model) {
                return value;
            }

            return new model(value);
        }
    },
    beforeDestroy() {
        this.isMounted = false;
    }
};
